import { useEffect, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useSocket from '../../hooks/useSocket';
import { AppDispatch, RootState } from '../../redux/store';
import { TOGGLE_CHAT_SCREEN } from '../../redux/reducers/chatScreenReducer';
import ChatScreen from '../chatScreen/ChatScreen';
import socket, { setSessionKey } from '../../socket';
import ToggleChatButton from '../toggleChatButton/ToggleChatButton';
import { CONSTANTS } from '../../constants/constants';

type Notification = {
  id: number;
  msg: string;
  followUpMsg?: string;
};

const MAX_DISPLAYED = Number(process.env.REACT_APP_MAX_DISPLAYED) ?? 3;
const AUTO_DISMISS_TIME = Number(process.env.REACT_APP_AUTO_DISMISS_TIME) || 3000;
const FOLLOW_UP_DELAY = Number(process.env.REACT_APP_FOLLOW_UP_DELAY || 5000);
const clientType = process.env.REACT_APP_CLIENT as string | undefined;

const ChatBot = () => {
  const { isConnected } = useSocket();
  const dispatch = useDispatch<AppDispatch>();
  const isChatOpen = useSelector((state: RootState) => state.chatScreen.isChatOpen);
  const events = useSelector((state: RootState) => state.events);

  // Core state
  const [displayed, setDisplayed] = useState<Notification[]>([]);
  const [, setMessageQueue] = useState<Notification[]>([]);
  const [idCounter, setIdCounter] = useState(0);

  // Refs for stable references
  const messageContainerRef = useRef<HTMLDivElement>(null);
  const dismissTimersRef = useRef<Map<number, NodeJS.Timeout>>(new Map());
  const processingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const followUpTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Follow-up message state
  const [awaitingUserResponse, setAwaitingUserResponse] = useState(false);
  const [baselineEventCount, setBaselineEventCount] = useState(0);

  // Client configuration
  const getClientConfig = (client: string) => {
    switch (client) {
      case 'salesbot2290ai':
        return {
          borderColor: '#1DBC91',
          borderColorWithOpacity: '#1DBC9180',
          arrowColor: '#1DBC91',
        };
      case 'salesbottax1099':
        return {
          borderColor: '#6491C3',
          borderColorWithOpacity: '#6491C380',
          arrowColor: '#6491C3',
        };
      default:
        return {
          borderColor: '#6491C3',
          borderColorWithOpacity: '#6491C380',
          arrowColor: '#6491C3',
        };
    }
  };

  const clientConfig = getClientConfig(clientType || '');

  // Helper function to send messages to parent
  const sendToParent = useCallback((type: string, payload: any) => {
    try {
      if (window.parent) {
        window.parent.postMessage({ type, payload }, '*');
        // if (process.env.NODE_ENV === 'development') {
          console.log(`📤 Sent ${type}:`, payload);
        // }
      }
    } catch (error) {
      console.error(`Failed to send ${type} to parent:`, error);
    }
  }, []);

  // Function to update parent dimensions
  const updateParentDimensions = useCallback(() => {


    // if(isChatOpen) {
    //   console.log("calculate dimensions", )
    //       const dimensions = {
    //   width: Math.max(600),
    //   height: Math.max(600),
    //   messageCount: displayed.length,
    //   action: displayed.length > 0 ? 'update' : 'reset',
    // };
    //   sendToParent('UPDATE_CHATBOT_DIMENSIONS', dimensions);
    // }
    if (!messageContainerRef.current || isChatOpen) return;

    const container = messageContainerRef.current;
    const rect = container.getBoundingClientRect();

    const maxAllowedWidth = Math.min(rect.width + 90, window.outerHeight * 0.6);
    const dimensions = {
      width: Math.max(maxAllowedWidth, 250),
      height: Math.max(rect.height + 30, 60),
      messageCount: displayed.length,
      action: displayed.length > 0 ? 'update' : 'reset',
    };

    sendToParent('UPDATE_CHATBOT_DIMENSIONS', dimensions);
  }, [displayed.length, isChatOpen, sendToParent]);

  // Clear all timers
  const clearAllTimers = useCallback(() => {
    dismissTimersRef.current.forEach(timer => clearTimeout(timer));
    dismissTimersRef.current.clear();

    if (processingTimeoutRef.current) {
      clearTimeout(processingTimeoutRef.current);
      processingTimeoutRef.current = null;
    }

    if (followUpTimeoutRef.current) {
      clearTimeout(followUpTimeoutRef.current);
      followUpTimeoutRef.current = null;
    }
  }, []);

  // Auto-dismiss a message and process next batch
  const dismissMessage = useCallback((id: number) => {
    setDisplayed(prev => {
      const newDisplayed = prev.filter(msg => msg.id !== id);

      // Clear the timer for this message
      const timer = dismissTimersRef.current.get(id);
      if (timer) {
        clearTimeout(timer);
        dismissTimersRef.current.delete(id);
      }

      return newDisplayed;
    });

    // Process next batch after a brief delay
    processingTimeoutRef.current = setTimeout(() => {
      setMessageQueue(prev => {
        if (prev.length === 0) return prev;

        const nextBatch = prev.slice(0, MAX_DISPLAYED);
        const remaining = prev.slice(MAX_DISPLAYED);

        setDisplayed(current => [...current, ...nextBatch]);

        // Set timers for new messages
        nextBatch.forEach(msg => {
          const timer = setTimeout(() => dismissMessage(msg.id), AUTO_DISMISS_TIME);
          dismissTimersRef.current.set(msg.id, timer);
        });

        return remaining;
      });
    }, 100);
  }, []);

  // Add new message to display or queue
  const addMessage = useCallback((msg: string, followUpMsg?: string) => {
    if (isChatOpen) return; // Don't show notifications when chat is open

    const newMessage: Notification = {
      id: idCounter,
      msg,
      followUpMsg,
    };

    setIdCounter(prev => prev + 1);

    setDisplayed(current => {
      if (current.length < MAX_DISPLAYED) {
        // Add to display directly
        const timer = setTimeout(() => dismissMessage(newMessage.id), AUTO_DISMISS_TIME);
        dismissTimersRef.current.set(newMessage.id, timer);
        return [...current, newMessage];
      } else {
        // Add to queue
        setMessageQueue(prev => [...prev, newMessage]);
        return current;
      }
    });
  }, [idCounter, isChatOpen, dismissMessage]);

  // Handle messages from parent
  const handlePostMessage = useCallback((event: MessageEvent) => {
    try {
      const { type, payload } = event.data || {};
      if (type === 'DISPLAY_MSG' && payload?.msg && !isChatOpen && events.length < 1) {
        console.log("DISPLAY_MSG payload", payload)
        addMessage(payload.msg, payload.followUpMsg);
      }
    } catch (error) {
      console.error('Error handling postMessage:', error);
    }
  }, [addMessage, isChatOpen, events]);

  // Check if user has replied since baseline
  const hasUserReplied = useCallback(() => {
    const recentEvents = events.slice(baselineEventCount);

    const result = recentEvents.some(
      event => event.senderType === "USER"
    );

    return result;
  }, [events, baselineEventCount]);


  // Handle initial message when chat opens
  const sendInitialMessage = useCallback(() => {
    if (!isChatOpen || events.length > 0) return;

    const welcomeData = clientType === 'salesbot2290ai'
      ? (displayed[displayed.length - 1] || CONSTANTS.WELCOME_MESSAGE)
      : (displayed[displayed.length - 1] || CONSTANTS.TAX1099_WELCOME_MESSAGE);

    if (welcomeData.msg) {
      socket.emit('bot_trigger_chat', welcomeData.msg);
      if (welcomeData.followUpMsg) {
        setBaselineEventCount(events.length);
        followUpTimeoutRef.current = setTimeout(() => {

          if (!hasUserReplied()) {
            socket.emit('bot_trigger_chat', welcomeData.followUpMsg);
          }
        }, FOLLOW_UP_DELAY);
      }
    }
  }, [isChatOpen, events.length, displayed, clientType, awaitingUserResponse, hasUserReplied]);

  // Effects
  useEffect(() => {
    if (!isConnected) {
      setSessionKey();
    }
  }, [isConnected]);

  useEffect(() => {
    console.log("OPEN_CHAT_WINDOW ===>", isChatOpen, "chatopen value", localStorage.getItem('is-chat-open'))

    if (isChatOpen) {
      console.log("OPEN_CHAT_WINDOW ===>", isChatOpen)
      sendToParent('OPEN_CHAT_WINDOW', { isChatOpen: true });
    }
    window.addEventListener('message', handlePostMessage);
    return () => window.removeEventListener('message', handlePostMessage);
  }, []);

  useEffect(() => {
    updateParentDimensions();
  }, [displayed.length, isChatOpen]);

  useEffect(() => {
    sendInitialMessage();
  }, [isChatOpen]);

  // Check for user response to cancel follow-up
  useEffect(() => {
    console.log("awaitingUserResponse changed", awaitingUserResponse, hasUserReplied())
    if (awaitingUserResponse && hasUserReplied()) {
      setAwaitingUserResponse(false);
      if (followUpTimeoutRef.current) {
        clearTimeout(followUpTimeoutRef.current);
        followUpTimeoutRef.current = null;
      }
    }
  }, [awaitingUserResponse]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearAllTimers();
    };
  }, [clearAllTimers]);

  // Reset dimensions when all messages are dismissed
  useEffect(() => {
    if (displayed.length === 0 && !isChatOpen) {
      sendToParent('UPDATE_CHATBOT_DIMENSIONS', {
        width: 80,
        height: 80,
        messageCount: 0,
        action: 'reset',
      });
    }
  }, [displayed.length, isChatOpen, sendToParent]);

  const onClose = () => {
    dispatch({ type: TOGGLE_CHAT_SCREEN });
  };

  const MessageDisplay = () => {
    if (displayed.length === 0) return null;

    return (
      <div
        ref={messageContainerRef}
        className="message-stack flex flex-col gap-[4px] relative items-end"
        style={{
          transition: 'all 0.5s ease-out',
          minWidth: '160px',
          width: 'max-content',
          maxWidth: '500px',
          willChange: 'transform',
          transform: 'translateZ(0)',
        }}
      >
        {displayed.map((msg, index) => {
          const isLast = index === displayed.length - 1;

          return (
            <div
              key={msg.id}
              className="mb-0 cursor-pointer"
              style={{
                position: 'relative',
                willChange: 'transform, opacity',
                transform: 'translateZ(0)',
                opacity: 1,
                animation: isLast
                  ? 'slideInFromRight 0.3s ease-out forwards'
                  : 'slideUpSmooth 0.3s ease-out forwards',
                animationDelay: isLast ? '0s' : `${index * 0.08}s`,
                // minWidth: 'fit-content',
              }}
            >
              <div
                style={{
                  backgroundColor: '#fff',
                  color: '#000',
                  padding: '12px 16px',
                  position: 'relative',
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  hyphens: 'auto',
                  border: `1px solid ${clientConfig.borderColorWithOpacity}`,
                  borderRadius: '9px',
                  marginBottom: '8px',
                  boxShadow: `-1px 4px 8px ${clientConfig.borderColorWithOpacity}`,
                  float: 'inline-end',
                  minWidth: 'fit-content',
                  maxWidth: '98%',
                  fontSize: 'clamp(12px, 2.5vw, 14px)',
                }}
              >
                <span
                  style={{
                    fontSize: 'inherit',
                    color: '#2c3e50',
                    fontFamily: 'lato',
                    fontWeight: 400,
                    lineHeight: '1.4',
                    display: 'block',
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                    hyphens: 'auto',
                    maxWidth: '100%',
                  }}
                >
                  {msg.msg}
                </span>

                {isLast && (
                  <>
                    <div
                      style={{
                        position: 'absolute',
                        top: '50%',
                        right: '-12px',
                        transform: 'translateY(-50%)',
                        width: 0,
                        height: 0,
                        borderTop: '10px solid transparent',
                        borderBottom: '10px solid transparent',
                        borderLeft: `12px solid ${clientConfig.borderColorWithOpacity}`,
                      }}
                    />
                    <div
                      style={{
                        position: 'absolute',
                        top: '50%',
                        right: '-11px',
                        transform: 'translateY(-50%)',
                        width: 0,
                        height: 0,
                        borderTop: '9px solid transparent',
                        borderBottom: '9px solid transparent',
                        borderLeft: '11px solid #fff',
                      }}
                    />
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div id="ai-chatbot" className="font-sans text-sm fixed bottom-6 right-6 z-[9999]">
      <div className="relative">
        {!isChatOpen && (
          <div className="absolute bottom-full right-[56px] mb-0 z-50">
            <MessageDisplay />
          </div>
        )}
        {!isChatOpen && <ToggleChatButton />}
      </div>

      <ChatScreen onClose={onClose} />
    </div>
  );
};

export default ChatBot;